import { makeAutoObservable, runInAction } from "mobx";
import {
  InventoryApiV1RoutersMatchSnapshotsListSnapshotsCategoryEnum,
  SnapshotListOUT,
  SnapshotOUT,
} from "api-client/generated/api";
import { getLocalAuthHeader, matchSnapshotsApi } from "api-client";
import { NotificationType } from "components/Toast";
import { MEStore } from "features/MappingEditor/MappingEditorStore";

export type SnapshotActionResult =
  | { ok: true }
  | { ok: false; error: unknown; message: string };

function normalizeSnapshotErrorMessage(error: unknown): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  const responseMessage = (
    error as { response?: { data?: { detail?: string } } }
  )?.response?.data?.detail;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }

  return "Unexpected error";
}

class MappingSnapshotStore {
  mappingSnapshots: SnapshotOUT[] = [];
  snapshots: SnapshotOUT[] = [];
  snapshotsPage = 1;
  snapshotsTotalPages = 1;
  hasMoreSnapshots = false;
  loading = false;
  snapshotsLoading = false;
  restoringId: number | null = null;

  private static instance: MappingSnapshotStore;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): MappingSnapshotStore {
    if (!MappingSnapshotStore.instance) {
      MappingSnapshotStore.instance = new MappingSnapshotStore();
    }
    return MappingSnapshotStore.instance;
  }

  private async fetchSnapshotList(
    fileId: string,
    page: number,
    pageSize: number,
    category: InventoryApiV1RoutersMatchSnapshotsListSnapshotsCategoryEnum
  ): Promise<
    | { ok: true; data: SnapshotListOUT }
    | { ok: false; error: unknown; message: string }
  > {
    try {
      const authHeader = getLocalAuthHeader();
      const response =
        await matchSnapshotsApi.inventoryApiV1RoutersMatchSnapshotsListSnapshots(
          fileId,
          page,
          pageSize,
          category,
          authHeader
        );
      return { ok: true, data: response.data };
    } catch (error) {
      console.error("Error fetching snapshots", error);
      return {
        ok: false,
        error,
        message: normalizeSnapshotErrorMessage(error),
      };
    }
  }

  async listMappingSnapshots(fileId: string): Promise<SnapshotActionResult> {
    this.loading = true;
    if (!fileId) {
      runInAction(() => {
        this.loading = false;
      });
      return {
        ok: false,
        error: "No file ID",
        message: "No file ID set",
      };
    }

    const result = await this.fetchSnapshotList(
      fileId,
      1,
      100,
      InventoryApiV1RoutersMatchSnapshotsListSnapshotsCategoryEnum.Mapping
    );

    runInAction(() => {
      if (result.ok) {
        this.mappingSnapshots = (result.data.snapshots ?? []) as SnapshotOUT[];
      }
      this.loading = false;
    });

    return result.ok ? { ok: true } : result;
  }

  async listSnapshots(
    fileId: string,
    reset = false
  ): Promise<SnapshotActionResult> {
    if (this.snapshotsLoading) {
      return { ok: true };
    }
    if (!fileId) {
      return {
        ok: false,
        error: "No file ID",
        message: "No file ID set",
      };
    }

    if (reset) {
      runInAction(() => {
        this.snapshots = [];
        this.snapshotsPage = 1;
        this.snapshotsTotalPages = 1;
        this.hasMoreSnapshots = false;
      });
    }
    this.snapshotsLoading = true;

    const result = await this.fetchSnapshotList(
      fileId,
      this.snapshotsPage,
      20,
      InventoryApiV1RoutersMatchSnapshotsListSnapshotsCategoryEnum.Full
    );

    runInAction(() => {
      if (result.ok) {
        const newSnapshots = (result.data.snapshots ?? []) as SnapshotOUT[];
        const currentPage = result.data.current_page ?? this.snapshotsPage;
        const totalPages = result.data.total_pages ?? 1;

        this.snapshots = [...this.snapshots, ...newSnapshots];
        this.snapshotsPage = currentPage + 1;
        this.snapshotsTotalPages = totalPages;
        this.hasMoreSnapshots = currentPage < totalPages;
      }
      this.snapshotsLoading = false;
    });

    return result.ok ? { ok: true } : result;
  }

  async restoreSnapshot(
    fileId: string,
    snapshotId: number,
    toast: (message: string, type: NotificationType) => void,
    successMessage: string
  ): Promise<SnapshotActionResult> {
    runInAction(() => {
      this.restoringId = snapshotId;
    });
    try {
      const authHeader = getLocalAuthHeader();
      await matchSnapshotsApi.inventoryApiV1RoutersMatchSnapshotsRecalculateSnapshot(
        fileId,
        snapshotId,
        true,
        authHeader
      );
      // Backend may clean up dangling mapping/unmapping snapshots during restore.
      // Always refresh mapping snapshots after a successful restore.
      await MEStore.getObjectsMappingStatus(fileId);
      await MEStore.getFileCalculationsStatus();
      toast(successMessage, "success");
      await this.listMappingSnapshots(fileId);
      return { ok: true };
    } catch (error) {
      console.error("Error restoring snapshot", error);
      return {
        ok: false,
        error,
        message: normalizeSnapshotErrorMessage(error),
      };
    } finally {
      runInAction(() => {
        this.restoringId = null;
      });
    }
  }

  async takeSnapshot(
    fileId: string,
    snapshotName: string,
    toast: (message: string, type: NotificationType) => void,
    successMessage: string
  ): Promise<SnapshotActionResult> {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const authHeader = getLocalAuthHeader();
      await matchSnapshotsApi.inventoryApiV1RoutersMatchSnapshotsTakeSnapshot(
        fileId,
        snapshotName,
        authHeader
      );
      toast(successMessage, "success");
      await this.listSnapshots(fileId, true);
      return { ok: true };
    } catch (error) {
      console.error("Error taking snapshot", error);
      return {
        ok: false,
        error,
        message: normalizeSnapshotErrorMessage(error),
      };
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async deleteSnapshot(
    fileId: string,
    snapshotId: number,
    toast: (message: string, type: NotificationType) => void,
    successMessage: string
  ): Promise<SnapshotActionResult> {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const authHeader = getLocalAuthHeader();
      await matchSnapshotsApi.inventoryApiV1RoutersMatchSnapshotsDeleteSnapshot(
        fileId,
        snapshotId,
        authHeader
      );
      toast(successMessage, "success");
      const listResult = await this.listSnapshots(fileId, true);
      if (!listResult.ok) {
        return listResult;
      }

      const mappingResult = await this.listMappingSnapshots(fileId);
      if (!mappingResult.ok) {
        return mappingResult;
      }
      return { ok: true };
    } catch (error) {
      console.error("Error deleting snapshot", error);
      return {
        ok: false,
        error,
        message: normalizeSnapshotErrorMessage(error),
      };
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}

export const mappingSnapshotStore = MappingSnapshotStore.getInstance();
